import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { List } from '@mui/material';
import AddCircleOutlineOutlined from '@mui/icons-material/AddCircleOutlineOutlined';
import { clsx } from 'clsx';
// Components
import EditReservationWrapper from './editReservationWrapper';
import PassengerCard from '../../../components/passengers/passengerCard';
import Loader from '../../../components/loader';
import Separator from '../../../components/layout/separator';
import Space, { Type } from '../../../components/layout/space';
import Text from '../../../components/text';
import SearchInput from '../../../components/searchInput';
import { PropsFromModal } from '../../../components/modals/createModalHOC';
import TextButton from '../../../components/buttons/textButton';
import TextCenterButton from '../../../components/buttons/textCenterButton';
import InputField from '../../../components/form/input';
import InfoBoxTex from '../../../components/infoBoxText';
// Queries
import { useUpdateRide } from '../../../core/services/rides';
import { useGetPolicies } from '../../../core/services/policies';
import { useGetCollabs, useGetRoles, useSearchCollabs } from '../../../core/services/contract';
import { useCreateCompanyGuest } from '../../../core/services/users';
// Resources
import {
    BaseUser,
    CreateCompanyGuestOutput,
    CreateMemberFamilyInput,
    EditRideInput,
    GetPoliciesOutput,
    Guest,
    IDGroupRole,
    IDNameRole,
    Ride,
    UserType,
} from '../../../core/apiTypes';
import { addRecentPassenger, getRecentPassengers, getUser } from '../../../localStorageAPI';
import { convertGuestToBaseUser, isRidePro } from '../../../utils/business';
import { stringsRes } from '../../../resources/strings';
import { getB2CContractID, getDefaultContractID } from '../../../utils/user';
import { WebLocation, useBreakpoints } from '../../../customHooks/useBreakpoints';
import { isMobileNumber, isPhoneValid, joinWithDash, regexMail } from '../../../utils/strings';
import { ToastWithIcon } from '../toastProvider';
import { MAX_USERS_NAMES, MIN_PHONE_ERROR } from '../../../utils/constants';

interface EditReservationPassengersProps extends PropsFromModal {
    reservation: Ride;
}
const EditReservationPassengers: FC = (props: EditReservationPassengersProps): ReactElement => {
    const { reservation, onRequestClose } = props;
    const classes = useStyles();
    const { isMobileSize } = useBreakpoints(WebLocation.FLOW);

    const isPro = isRidePro(reservation);
    const [selectedPassengers, setSelectedPassengers] = useState<BaseUser[]>([]);
    const [showInvite, setShowInvite] = useState<boolean>(false);
    const [searchInput, setSearchInput] = useState('');
    const [inputUserError, setUserError] = useState<string>('');

    const user = getUser();
    const recentPassengers = getRecentPassengers();

    const updateRide = useUpdateRide();
    const { data: roleList } = useGetRoles<Omit<IDGroupRole, 'since'>[]>();
    const { data: groupPolicyDatas } = useGetPolicies<GetPoliciesOutput>({
        enabled: !!reservation.group?.uuid,
        variables: { groups: [reservation?.group?.uuid] },
    });
    const groupPolicy = groupPolicyDatas?.group?.[0];
    const canInviteSomeone = (isPro && groupPolicy?.book_for_group_members) || groupPolicy?.book_for_group_guests;

    const {
        data: searchResults,
        isFetching: isFetchingColalbs,
        refetch,
    } = useSearchCollabs<any>({
        enabled: false,
        variables: {
            id: getDefaultContractID(),
            user_types: [
                ...(groupPolicy?.book_for_group_guests ? [UserType.GUEST] : []),
                ...(groupPolicy?.book_for_group_members ? [UserType.COWORKER] : []),
            ],
            pattern: searchInput,
            groups: [reservation.group.uuid],
            roles: [roleList?.find((el) => el.role === IDNameRole.RIDER).uuid],
        },
    });

    const { data: family, isFetching: isFetchingFamily } = useGetCollabs<CreateMemberFamilyInput[]>({
        variables: {
            id: getB2CContractID(user),
        },
    });

    useEffect(() => {
        if (family?.length > 0 && reservation?.riders?.length > 0) {
            setSelectedPassengers(reservation.riders);
        }
    }, [family, reservation]);

    const onSelectPassenger = (passenger: BaseUser) => {
        // mutli selection
        // const tmp = [...selectedPassengers];
        // const foundAtIndex = tmp.findIndex((p) => p.id === passenger.id);
        // if (foundAtIndex >= 0) {
        //     tmp.splice(foundAtIndex, 1);
        // } else {
        //     tmp.push(passenger);
        // }
        // setSelectedPassengers(tmp);

        // single selection
        const tmp = [...selectedPassengers];
        const foundAtIndex = tmp.findIndex((p) => p.id === passenger.id);
        if (foundAtIndex >= 0) {
            tmp.splice(foundAtIndex, 1);
        } else {
            tmp.splice(0, 1, passenger);
        }
        setSelectedPassengers(tmp);
    };

    const onConfirm = (): void => {
        const payload: EditRideInput = {
            id: reservation.id,
            riders: selectedPassengers,
        };

        updateRide.mutate(payload, {
            onSuccess: () => {
                if (isPro && selectedPassengers?.[0] && user.id !== selectedPassengers?.[0]?.id) {
                    addRecentPassenger(selectedPassengers);
                }
                onRequestClose();
            },
        });
    };

    const renderPassengers = (): JSX.Element | JSX.Element[] => {
        if (isFetchingFamily) {
            return <Loader />;
        }

        let possiblePassengers = [...(family || [])];
        if (isPro) {
            possiblePassengers = [];
        }

        if (!possiblePassengers.find((p) => p.id === reservation.riders[0].id)) {
            possiblePassengers.unshift({
                firstname: reservation.riders[0].firstname,
                lastname: reservation.riders[0].lastname,
                phone: reservation.riders[0].phone_number,
                id: reservation.riders[0].id,
            });
        }

        if (selectedPassengers.length && !possiblePassengers.find((p) => p.id === selectedPassengers?.[0]?.id)) {
            possiblePassengers.unshift({
                firstname: selectedPassengers[0].firstname,
                lastname: selectedPassengers[0].lastname,
                phone: selectedPassengers[0].phone_number,
                id: selectedPassengers[0].id,
            });
        }

        if (reservation.riders[0].id !== user.id && !possiblePassengers.find((pp) => pp.id === user.id)) {
            possiblePassengers.unshift({
                firstname: user.firstname,
                lastname: user.lastname,
                phone: user.phones[0]?.phone,
                id: user.id,
            });
        }

        return (
            possiblePassengers &&
            possiblePassengers.map((p): JSX.Element => {
                return (
                    <PassengerCard
                        key={p.id}
                        passenger={p}
                        onSelect={onSelectPassenger}
                        isSelected={selectedPassengers.findIndex((passenger) => passenger.id === p.id) !== -1}
                    />
                );
            })
        );
    };

    const recentPassengersRows = useMemo(() => {
        return recentPassengers
            ?.filter((p) =>
                selectedPassengers?.[0]
                    ? p.id !== selectedPassengers[0]?.id
                    : reservation?.riders?.[0]
                      ? p.id !== reservation.riders[0]?.id
                      : true,
            )
            ?.filter((p) =>
                [
                    ...(groupPolicy?.book_for_group_guests ? [UserType.GUEST] : []),
                    ...(groupPolicy?.book_for_group_members ? [UserType.COWORKER] : []),
                ].includes(p.user_type as UserType),
            )
            ?.map((p): JSX.Element => {
                const subtitleDatas = joinWithDash(
                    stringsRes.userTypes[p.user_type],
                    p.phone_number,
                    p.email?.startsWith('dev+') ? undefined : p.email,
                );
                return (
                    <PassengerCard
                        key={p.id}
                        passenger={p}
                        subtitle={subtitleDatas}
                        onSelect={onSelectPassenger}
                        isSelected={selectedPassengers.findIndex((passenger) => passenger.id === p.id) !== -1}
                    />
                );
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recentPassengers]);

    React.useEffect(() => {
        if (searchInput?.length > 0) {
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    const onSearch = (value: string) => {
        setSearchInput(value);
    };

    //= =====================================//
    //= ============== GUESTS ===============//
    //= =====================================//

    const onAddNewMember = () => {
        setShowInvite(true);
    };

    const createGuest = useCreateCompanyGuest({ onError: () => {} });

    const [newGuest, setGuest] = useState<Guest>({
        firstname: '',
        lastname: '',
        email: '',
        phone_e164: '',
        language: 'fr',
    });

    const onBlur = (): void => {
        if (!isPhoneValid(newGuest.phone_e164, newGuest.language)) {
            setUserError(stringsRes.errors.phoneInvalidError);
        }
    };

    const onChangeGuest = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setUserError('');
        const { value, name } = event.target;
        const { data } = event.target as any; // For tel input we have more datas
        setGuest({ ...newGuest, [name]: value, ...(data?.countryCode && { language: data?.countryCode }) });
    };

    const onGuestCancel = () => {
        setShowInvite(false);
    };

    const onConfirmAction = () => {
        createGuest.mutate(
            { id: getDefaultContractID(), groupId: reservation?.group?.uuid, guests: [newGuest] },
            {
                onSuccess: (data: CreateCompanyGuestOutput) => {
                    console.log(data);
                    setSelectedPassengers(convertGuestToBaseUser(data?.processed, true));
                    setShowInvite(false);
                },
                onError: () => {
                    ToastWithIcon('error', stringsRes.common.retryLater);
                },
            },
        );
    };

    const isGuestComplete = (): boolean => {
        return newGuest.firstname !== '' && newGuest.lastname !== '' && newGuest.phone_e164 !== '';
    };

    // Show Invitation page
    if (showInvite) {
        return (
            <div className={classes.addGuestInFlowRoot}>
                <Text.Description16 customColor="grey" text={stringsRes.booking.addProPassengerDescription} />
                <Space value={2} />
                <InputField
                    autoFocus
                    onChange={onChangeGuest}
                    name="firstname"
                    required
                    value={newGuest.firstname}
                    label={stringsRes.family.firstname}
                    inputProps={{ maxLength: MAX_USERS_NAMES }}
                />
                <InputField
                    onChange={onChangeGuest}
                    name="lastname"
                    required
                    value={newGuest.lastname}
                    label={stringsRes.family.lastname}
                    inputProps={{ maxLength: MAX_USERS_NAMES }}
                />
                <InputField
                    onChange={onChangeGuest}
                    name="email"
                    value={newGuest.email}
                    label={`${stringsRes.profile.email} ${stringsRes.common.optional}`}
                    test={{ regex: regexMail, label: stringsRes.errors.invalidFormat }}
                />
                <InputField
                    onChange={onChangeGuest}
                    onBlur={onBlur}
                    errorText={inputUserError}
                    name="phone_e164"
                    required
                    value={newGuest.phone_e164}
                    placeholder={stringsRes.profile.phoneNumber}
                    type="tel"
                    mode="tel"
                    isValidFn={(_val, countryData) => {
                        const value = newGuest.phone_e164;
                        const selectedCountry = newGuest.language;
                        if (countryData.iso2 !== selectedCountry) {
                            setGuest({ ...newGuest, language: countryData.iso2 });
                        }
                        if (value.length > MIN_PHONE_ERROR && !isMobileNumber(value, countryData.iso2)) {
                            setUserError(stringsRes.errors.phoneHomeError);
                            return false;
                        }
                        return true;
                    }}
                />
                <Space value={2.5} />
                <InfoBoxTex description={stringsRes.booking.phoneHint} />
                <Space value={1} />
                <div className={classes.confirmButtonContainer}>
                    <TextCenterButton
                        title={stringsRes.common.back}
                        id="back"
                        onClick={onGuestCancel}
                        customColor="grey"
                    />
                    <Space value={1} type={Type.horizontal} />
                    <TextCenterButton
                        title={stringsRes.booking.valid}
                        id="valid"
                        onClick={onConfirmAction}
                        disabled={
                            !isGuestComplete() ||
                            !isPhoneValid(newGuest.phone_e164, newGuest.language) ||
                            !isMobileNumber(newGuest.phone_e164, newGuest.language)
                        }
                    />
                </div>
            </div>
        );
    }

    return (
        <EditReservationWrapper
            {...props}
            onConfirm={onConfirm}
            loadingConfirm={updateRide.isLoading}
            disableConfirm={selectedPassengers?.length < 1}
            fixedHeight
            childrenContainerClassName={classes.passengersContainer}
        >
            {/* <Text.Description16 customColor="grey" text={stringsRes.booking.selectPassengers} />
            <Space value={2} /> */}
            {renderPassengers()}
            {isPro && groupPolicy?.book_for_group_guests && (
                <TextButton
                    title={stringsRes.booking.addProPassenger}
                    id="addGuest"
                    className={clsx(classes.contentContainer, isMobileSize && classes.contentContainerResponsive)}
                    onClick={onAddNewMember}
                    startIcon={<AddCircleOutlineOutlined />}
                />
            )}
            {canInviteSomeone && (
                <>
                    <Separator height={8} />
                    <Space value={6} />
                    <div>
                        <Text.H25 text={stringsRes.booking.bookForOther} />
                        <Space value={2} />
                        <SearchInput
                            intervalCharInput={3}
                            label={`${
                                groupPolicy?.book_for_group_guests && groupPolicy?.book_for_group_members
                                    ? stringsRes.booking.searchPassengerCoworkerOrGuest
                                    : groupPolicy?.book_for_group_members
                                      ? stringsRes.booking.searchPassengerCoworker
                                      : stringsRes.booking.searchPassengerGuest
                            }`}
                            intervalTimeInput={500}
                            minCharInput={3}
                            searchAction={onSearch}
                        />
                        <Space value={2} />
                    </div>
                    {isFetchingColalbs || (isFetchingColalbs && !searchResults) ? (
                        <Loader containerClassName={classes.bookingPassengerLoader} />
                    ) : (
                        <>
                            {!!searchResults?.length && (
                                <List>
                                    {searchResults?.map((p): JSX.Element => {
                                        const subtitleDatas = joinWithDash(
                                            stringsRes.userTypes[p.user_type],
                                            p.phone_number,
                                            p.email,
                                        );
                                        return (
                                            <PassengerCard
                                                key={p.id}
                                                passenger={p}
                                                subtitle={subtitleDatas}
                                                onSelect={onSelectPassenger}
                                                isSelected={
                                                    selectedPassengers.findIndex(
                                                        (passenger) => passenger.id === p.id,
                                                    ) !== -1
                                                }
                                            />
                                        );
                                    })}
                                </List>
                            )}
                            {(!searchResults || searchResults.length <= 0) && (
                                <>
                                    <Text.Description14
                                        customColor="grey"
                                        text={stringsRes.common.noResult}
                                        containerClassName={classes.bookingPassengersCenter}
                                    />
                                    {recentPassengersRows?.length > 0 && isPro && (
                                        <>
                                            <Text.ShortRegularText16
                                                text={stringsRes.booking.searchRecent}
                                                customColor="grey"
                                                className={clsx(
                                                    classes.contentContainer,
                                                    isMobileSize && classes.contentContainerResponsive,
                                                )}
                                            />
                                            <List className={classes.recentPassengersList}>{recentPassengersRows}</List>
                                        </>
                                    )}
                                </>
                            )}
                            {searchResults?.length > 0 && <Space value={2} />}
                        </>
                    )}
                </>
            )}
        </EditReservationWrapper>
    );
};

const useStyles = makeStyles(() =>
    createStyles({
        contentContainer: {
            padding: '16px 30px 16px 30px',
        },
        contentContainerResponsive: {
            paddingLeft: 20,
            paddingRight: 20,
        },
        confirmButtonContainer: {
            display: 'flex',
            flexDirection: 'row',
            padding: 30,
            textAlign: 'center',
        },
        bookingPassengerLoader: {
            overflowY: 'hidden',
        },
        bookingPassengersCenter: {
            justifyContent: 'center',
        },
        addGuestInFlowRoot: {
            padding: '24px 30px 8px 30px',
        },
        recentPassengersList: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 200,
            overflow: 'auto',
        },
        passengersContainer: {
            gap: 8,
        },
    }),
);

const mapStateToProps = () => ({});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(EditReservationPassengers);
